import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { ResourceAllocationStatus } from '~/types';
import {
  ChartRangeItem,
  RangeBoundaries
} from '~/modules/common/charts/timeline/components';
import { hasPermission } from '~/modules/common/permissions';
import { mapIsoStringtoUtcObject } from '~/modules/common/dates/convert';
import { useAllocationTimelineBlocks } from '~/modules/resourcing/common/chart/hooks';
import {
  useQuickAllocationContext,
  useResourceAllocationUpdateHandler
} from '~/modules/quickAllocation/components/QuickAllocationUserChart/hooks';
import { ResourceRequestUserAllocationOverlay } from '~/modules/resourcing/common/components/ResourceRequestUserAllocationBlock/components/';
import ResourceActualHoursSummaryBlocks from '~/modules/resourcing/common/components/ResourceActualHoursSummaryBlocks/ResourceActualHoursSummaryBlocks';
import { useMeContext } from '~/modules/me';
import QuickAllocationUserProjectSummaryBlocks from '../QuickAllocationUserProjectSummaryBlocks';
import QuickAllocationProjectGroupLeftComponent from './QuickAllocationProjectGroupLeftComponent';

const useBoundaryStyles = makeStyles(() => ({
  startBoundary: {
    border: '0.25px solid #c6c6c6'
  },
  endBoundary: {
    border: '0.25px solid #c6c6c6'
  }
}));

const useOverlayStyles = makeStyles(theme => ({
  overlay: {
    zIndex: 1,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)'
    },
    height: theme.spacing(3),
    marginTop: theme.spacing(1)
  }
}));

const useStyles = makeStyles(theme => ({
  timelineContainer: {
    display: 'flex',
    minHeight: ({ minHeigthOverride }) => theme.spacing(minHeigthOverride)
  }
}));

const QuickAllocationProjectGroupDetailRow = ({
  rangeMinDate,
  overrideLeftComponent,
  rangeMaxDate,
  resourceAllocation,
  resourceUser,
  resourceAllocatedHoursByDateRange = [],
  timeoffSummaryPeriods = [],
  holidaySummaryPeriods = [],
  allocationSort,
  resourceAvailabilitySummarySeries,
  handleAllocationPeriodClick: onAllocationClick,
  resourceUserActualSeries
}) => {
  const boundaryClasses = useBoundaryStyles();
  const me = useMeContext();
  const { isPSARMPResourceActualEnabled } = me.featureFlags;
  const { onUpdateResourceAllocation } = useResourceAllocationUpdateHandler({
    allocationSort
  });
  const isResourceAssignmentEnabled = hasPermission(me.permissionsMap)({
    actionUri:
      'urn:replicon:resource-assignment-action:edit-resource-assignments'
  });
  const isCompleteResourceAllocationPermissionEnabled = hasPermission(
    me.permissionsMap
  )({
    actionUri: 'urn:replicon-webui:completed-resource-allocation:edit'
  });

  const isEditable =
    (resourceAllocation.allocationStatus ===
      ResourceAllocationStatus.Committed &&
      isCompleteResourceAllocationPermissionEnabled) ||
    (isResourceAssignmentEnabled &&
      resourceAllocation.allocationStatus !==
        ResourceAllocationStatus.Committed);

  const {
    scale,
    chartDates,
    leftComponentWidth,
    chartStartDate,
    chartEndDate,
    setAvailabilityLoading,
    isShowActualEnabled,
    dateRange: chartDisplayDateRange
  } = useQuickAllocationContext();

  const handleAllocationPeriodClick = param =>
    onAllocationClick({
      ...param,
      resourceUser,
      resourceAllocation,
      allocationSort
    });

  const onAllocationChange = useCallback(
    evt => {
      const updateAllocation = {
        ...evt.values,
        requestedRoleUri: resourceAllocation.role?.uri,
        resourceRequestId: null
      };

      if (evt.values) {
        delete updateAllocation.project;
        delete updateAllocation.role;
      }
      setAvailabilityLoading();
      onUpdateResourceAllocation({
        allocation: updateAllocation,
        existingResourceAllocation: resourceAllocation
      });
    },
    [resourceAllocation, onUpdateResourceAllocation, setAvailabilityLoading]
  );
  const classes = useStyles({
    minHeigthOverride: isShowActualEnabled ? 10 : 5
  });
  const overlayClasses = useOverlayStyles();

  const leftComponent = useMemo(
    () => (
      <QuickAllocationProjectGroupLeftComponent
        resourceAllocation={resourceAllocation}
        resourceUser={resourceUser}
        allocationSort={allocationSort}
        scale={scale}
      />
    ),
    [resourceAllocation, resourceUser, allocationSort, scale]
  );

  const allocationProject = resourceAllocation.project || {};

  const minDate =
    mapIsoStringtoUtcObject(allocationProject.startDate) || rangeMinDate;

  const maxDate =
    mapIsoStringtoUtcObject(allocationProject.endDate) || rangeMaxDate;
  const { allocationPeriods } = useAllocationTimelineBlocks({
    allocation: resourceAllocation,
    allocationScheduleDurationHours:
      resourceAllocation.user && resourceAllocation.user.scheduleDurationByDay,
    chartStartDate,
    showTotalCost: false,
    periodStartDate: chartStartDate,
    periodEndDate: chartEndDate,
    scale
  });

  const timeLineComponent = useMemo(
    () => (
      <div
        className={classNames(
          classes.timelineContainer,
          resourceAllocation.project &&
            isEditable &&
            'resourceRequestUserAllocation' // to support tabbing to find closest next container
        )}
      >
        <RangeBoundaries
          classes={boundaryClasses}
          scale={scale}
          chartStartDate={chartStartDate}
          start={minDate.toISO()}
          end={maxDate.toISO()}
        />
        {!resourceAllocation.isNew && (
          <>
            <ResourceRequestUserAllocationOverlay
              allocation={resourceAllocation}
              isEditable={isEditable}
              classes={overlayClasses}
              scale={scale}
              requestPeriods={allocationPeriods}
              handleAllocationPeriodClick={handleAllocationPeriodClick}
              resourceAvailabilitySummarySeries={
                resourceAvailabilitySummarySeries
              }
            />
            <QuickAllocationUserProjectSummaryBlocks
              resourceAllocation={resourceAllocation}
              onAllocationChange={onAllocationChange}
              resourceAllocatedHoursByDateRange={
                resourceAllocatedHoursByDateRange
              }
              holidaySummaryPeriods={holidaySummaryPeriods}
              timeoffSummaryPeriods={timeoffSummaryPeriods}
              chartDisplayDateRange={chartDisplayDateRange}
              overlayPeriods={allocationPeriods}
              resourceAvailabilitySummarySeries={
                resourceAvailabilitySummarySeries
              }
              handleAllocationPeriodClick={handleAllocationPeriodClick}
            />
            {isPSARMPResourceActualEnabled && isShowActualEnabled && (
              <ResourceActualHoursSummaryBlocks
                dataQeId="ProjectRow"
                chartStartDate={chartStartDate}
                scale={scale}
                resourceUser={resourceUser}
                allocationPeriods={allocationPeriods}
                resourceUserActualTimePeriods={
                  resourceUserActualSeries && resourceUserActualSeries.periods
                }
                resourceAvailabilityTimePeriods={
                  resourceAvailabilitySummarySeries
                }
              />
            )}
          </>
        )}
      </div>
    ),
    [
      classes.timelineContainer,
      resourceAllocation,
      isEditable,
      boundaryClasses,
      scale,
      chartStartDate,
      minDate,
      maxDate,
      overlayClasses,
      allocationPeriods,
      handleAllocationPeriodClick,
      resourceAvailabilitySummarySeries,
      onAllocationChange,
      resourceAllocatedHoursByDateRange,
      holidaySummaryPeriods,
      timeoffSummaryPeriods,
      chartDisplayDateRange,
      isPSARMPResourceActualEnabled,
      isShowActualEnabled,
      resourceUser,
      resourceUserActualSeries
    ]
  );

  return (
    <ChartRangeItem
      scale={scale}
      isFixedRange
      leftComponentWidth={leftComponentWidth}
      scaleItemCount={chartDates.length}
      chartStartDate={chartStartDate}
      leftComponent={overrideLeftComponent || leftComponent}
      timeItems={timeLineComponent}
    />
  );
};

QuickAllocationProjectGroupDetailRow.propTypes = {
  resourceAllocation: PropTypes.object.isRequired,
  resourceUser: PropTypes.object,
  rangeMinDate: PropTypes.object,
  rangeMaxDate: PropTypes.object,
  overrideLeftComponent: PropTypes.node,
  resourceAllocatedHoursByDateRange: PropTypes.array,
  timeoffSummaryPeriods: PropTypes.array,
  holidaySummaryPeriods: PropTypes.array,
  allocationSort: PropTypes.object,
  resourceAvailabilitySummarySeries: PropTypes.array,
  handleAllocationPeriodClick: PropTypes.func,
  resourceUserActualSeries: PropTypes.object
};

export default QuickAllocationProjectGroupDetailRow;
